import { useEffect, useState } from 'react';
import { useCustomComponentContext } from './CustomComponentProvider';
import type { LoadedCustomComponentModule } from './hostRuntime';

type CustomComponentLoadState =
  | { status: 'no-provider' }
  | { status: 'idle' }
  | { status: 'loading'; slug: string; sourceHash: string }
  | { status: 'ready'; slug: string; sourceHash: string; module: LoadedCustomComponentModule }
  | { status: 'error'; slug: string; error: Error };

/**
 * Loads the compiled artifact for a tree node's pinned `customComponentSourceHash`. The hash is
 * what actually selects the artifact bytes; the slug is for human-readable telemetry only. The
 * provider's `resolveCompiledUrl(sourceHash)` is the source of truth for where to fetch — no
 * manifest lookup is gating the load. A non-existent hash surfaces as a fetch failure → `error`.
 *
 * Re-fires when either the slug or the hash changes (Yjs undo flips both atomically). Stale
 * responses are discarded with the `cancelled` flag so a fast edit-then-edit doesn't flash the
 * intermediate module. Returns `no-provider` when there's no `CustomComponentProvider` in the
 * tree — the renderer surfaces that as a clear placeholder without crashing.
 */
export function useCustomComponentModule(slug: string, sourceHash: string): CustomComponentLoadState {
  const ctx = useCustomComponentContext();
  const loadModuleByHash = ctx?.loadModuleByHash ?? null;

  const [state, setState] = useState<CustomComponentLoadState>(() => {
    if (!ctx) return { status: 'no-provider' };
    if (!sourceHash) return { status: 'idle' };
    return { status: 'loading', slug, sourceHash };
  });

  useEffect(() => {
    if (!ctx || !loadModuleByHash) {
      setState({ status: 'no-provider' });
      return () => undefined;
    }
    if (!sourceHash) {
      // Empty pin — Insert-menu placeholder before the user picks a slug. Surface as idle so the
      // renderer's caller can show its own placeholder without us flashing a load attempt.
      setState({ status: 'idle' });
      return () => undefined;
    }

    let cancelled = false;
    setState({ status: 'loading', slug, sourceHash });
    loadModuleByHash(sourceHash)
      .then((module) => {
        if (cancelled) return;
        setState({ status: 'ready', slug, sourceHash, module });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          slug,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [ctx, slug, sourceHash, loadModuleByHash]);

  return state;
}
